import {
  accountLoginSchema,
  accountRegistrationSchema,
  accountLogoutSchema,
  accountOAuthSchema,
  accountPopupSchema,
  accountMenuSchema,
} from '@integration-layer/schemas/GAEvents/accountEvents'

/**
 * Provides methods for tracking various Google Analytics account-related events.
 *
 * @function useGAAccountEvents
 * @returns {Object} An object containing methods to dispatch different account events.
 *
 * @property {Function} dispatchAccountLoginEvent - Tracks login button click event.
 * @property {Function} dispatchAccountRegistrationEvent - Tracks registration popup view event.
 * @property {Function} dispatchAccountLogoutEvent - Tracks logout button click event.
 * @property {Function} dispatchAccountOAuthEvent - Tracks OAuth login with social icon.
 * @property {Function} dispatchAccountMenuEvent - Tracks account menu interactions.
 * @property {Function} dispatchAccountPopupLoginEvent - Tracks login popup interactions.
 *
 * @example
 * const {
 *   dispatchAccountLoginEvent,
 *   dispatchAccountOAuthEvent
 * } = useGAAccountEvents()
 *
 * dispatchAccountLoginEvent()
 * dispatchAccountOAuthEvent('google')
 */
export const useGAAccountEvents = () => {
  const debug = !!useRuntimeConfig().public.ga4.debug

  /**
   * Dispatches a Google Analytics event for account login.
   *
   * @function dispatchAccountLoginEvent
   * @returns {void}
   */
  const dispatchAccountLoginEvent = () => {
    useGASendEvent(
      accountLoginSchema,
      {
        event: 'GAevent',
        eventID: '001',
        eventCategory: 'login',
        eventAction: 'click_cta',
        eventLabel: '-',
        eventElement01: '-',
        eventElement02: '-',
        eventElement03: '-',
      },
      { debug, waitForDatalayerSetup: true }
    )
  }

  /**
   * Dispatches a Google Analytics event for account registration.
   *
   * @function dispatchAccountRegistrationEvent
   * @returns {void}
   */
  const dispatchAccountRegistrationEvent = () => {
    useGASendEvent(
      accountRegistrationSchema,
      {
        event: 'GAevent',
        eventID: '002',
        eventCategory: 'registration',
        eventAction: 'view_element',
        eventLabel: 'popup',
        eventElement01: '-',
        eventElement02: '-',
        eventElement03: '-',
      },
      { debug }
    )
  }

  /**
   * Dispatches a Google Analytics event for account logout.
   *
   * @function dispatchAccountLogoutEvent
   * @returns {void}
   */
  const dispatchAccountLogoutEvent = () => {
    useGASendEvent(
      accountLogoutSchema,
      {
        event: 'GAevent',
        eventID: '003',
        eventCategory: 'logout',
        eventAction: 'click_cta',
        eventLabel: '-',
        eventElement01: '-',
        eventElement02: '-',
        eventElement03: '-',
      },
      { debug }
    )
  }

  /**
   * Dispatches a Google Analytics event for OAuth login.
   *
   * @function dispatchAccountOAuthEvent
   * @param {string} socialIcon - The name of the social platform used for OAuth.
   * @returns {void}
   */
  const dispatchAccountOAuthEvent = (socialIcon: string) => {
    useGASendEvent(
      accountOAuthSchema,
      {
        event: 'GAevent',
        eventID: '021',
        eventCategory: 'oauth',
        eventAction: 'click_cta',
        eventLabel: socialIcon,
        eventElement01: '-',
        eventElement02: '-',
        eventElement03: '-',
      },
      { debug }
    )
  }

  /**
   * Dispatches a Google Analytics event for account menu interactions.
   *
   * @function dispatchAccountMenuEvent
   * @param {string} accountIcon - The identifier for the account menu icon.
   * @returns {void}
   */
  const dispatchAccountMenuEvent = (accountIcon: string) => {
    useGASendEvent(
      accountMenuSchema,
      {
        event: 'GAevent',
        eventID: '022',
        eventCategory: 'account_menu',
        eventAction: 'click_cta',
        eventLabel: accountIcon ?? '',
        eventElement01: '-',
        eventElement02: '-',
        eventElement03: '-',
      },
      { debug }
    )
  }

  /**
   * Dispatches a Google Analytics event for login popup interactions.
   *
   * @function dispatchAccountPopupLoginEvent
   * @param {string} loginIcon - The identifier for the login icon.
   * @returns {void}
   */
  const dispatchAccountPopupLoginEvent = (loginIcon: string) => {
    useGASendEvent(
      accountPopupSchema,
      {
        event: 'GAevent',
        eventID: '023',
        eventCategory: 'popup_login',
        eventAction: 'click_cta',
        eventLabel: loginIcon ?? '',
        eventElement01: '-',
        eventElement02: '-',
        eventElement03: '-',
      },
      { debug }
    )
  }

  return {
    dispatchAccountLoginEvent,
    dispatchAccountRegistrationEvent,
    dispatchAccountLogoutEvent,
    dispatchAccountOAuthEvent,
    dispatchAccountMenuEvent,
    dispatchAccountPopupLoginEvent,
  }
}
